import { useState } from 'react';
import { mobileSupabase as supabase } from '@/integrations/supabase/mobile-client';
import { useToast } from '@/hooks/use-toast';
import { Family, FamilyMember } from '../types';
import { logger } from '@/utils/logger';

export const useFamilyMembers = (
  family: Family | null,
  onSuccess: () => void
) => {
  const { toast } = useToast();
  const [isInviting, setIsInviting] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  const inviteFamilyMember = async (inviteEmail: string) => {
    if (!inviteEmail.trim() || !family) {
      toast({
        title: 'Missing Information',
        description: 'Please enter a valid email address',
        variant: 'destructive'
      });
      return;
    }
    
    setIsInviting(true);
    
    try {
      // Format email to lowercase for consistency
      const formattedEmail = inviteEmail.trim().toLowerCase();
      
      logger.log('Checking if email already exists:', formattedEmail);
      
      // First check if this email is already a member or invited (by email field)
      const { data: existingByEmail, error: emailCheckError } = await supabase
        .from('family_members')
        .select('*')
        .eq('family_id', family.id)
        .eq('email', formattedEmail);
      
      if (emailCheckError) {
        logger.error('Error checking existing invites by email:', emailCheckError);
        throw emailCheckError;
      }
      
      if (existingByEmail && existingByEmail.length > 0) {
        toast({
          title: 'Already Invited',
          description: 'This email has already been invited to your family',
          variant: 'destructive'
        });
        setIsInviting(false);
        return;
      }
      
      // Get current user to create invitation
      const { data: userData } = await supabase.auth.getUser();
      if (!userData?.user?.id) {
        toast({
          title: 'Error',
          description: 'You must be logged in to invite members',
          variant: 'destructive'
        });
        setIsInviting(false);
        return;
      }
      
      // Generate a random temporary ID for this invitation
      // This avoids the duplicate key constraint when we have multiple invites from same user
      const tempUserId = crypto.randomUUID();
      
      // Create the invitation with temporary user_id (will be updated when accepted)
      const { error: inviteError } = await supabase
        .from('family_members')
        .insert({
          family_id: family.id,
          email: formattedEmail,
          role: 'member',
          status: 'pending',
          // Use the temporary ID instead of current user's ID
          user_id: tempUserId
        });
      
      if (inviteError) {
        logger.error('Error inviting family member:', inviteError);
        throw inviteError;
      }
      
      toast({
        title: 'Success',
        description: 'Invitation sent successfully!'
      });
      
      onSuccess();
    } catch (error: unknown) {
      logger.error('Error in inviteFamilyMember:', error);
      const message = error instanceof Error ? error.message : 'Unknown error';
      toast({
        title: 'Error',
        description: 'Could not send invitation: ' + message,
        variant: 'destructive'
      });
    } finally {
      setIsInviting(false);
    }
  };
  
  const removeFamilyMember = async (memberToRemove: FamilyMember) => {
    if (!memberToRemove || !family) {
      return;
    }
    
    setIsRemoving(true);
    
    try {
      const { error } = await supabase
        .from('family_members')
        .delete()
        .eq('id', memberToRemove.id);
      
      if (error) {
        logger.error('Error removing family member:', error);
        toast({
          title: 'Error',
          description: 'Could not remove family member',
          variant: 'destructive'
        });
        return;
      }
      
      toast({
        title: 'Success',
        description: 'Family member removed successfully!'
      });
      
      onSuccess();
    } catch (error) {
      logger.error('Error in removeFamilyMember:', error);
      toast({
        title: 'Error',
        description: 'An unexpected error occurred',
        variant: 'destructive'
      });
    } finally {
      setIsRemoving(false);
    }
  };

  return {
    isInviting,
    isRemoving,
    inviteFamilyMember,
    removeFamilyMember
  };
};
